import {
  CoasterProjectV1Schema,
  canonicalStringify,
  hashCoasterProject,
  hashCoasterProjectV1,
  loadCoasterProject,
  migrateV1ToV2,
  parseCoasterProject,
  type CoasterProject,
} from '../schema/index.ts';
import {
  LEGACY_V1_DRAFT_NAMESPACE,
  LocalWorkingDraftRepository,
  type DraftRepositoryResult,
  type StoredDraft,
  type StoredDraftSummary,
  type WorkingDraftRepository,
} from './working-draft-repository.ts';

export type DraftDurability = 'durable' | 'limited' | 'session_only';

export interface RepositoryInitialization {
  readonly durability: DraftDurability;
  readonly importedLegacyDrafts: number;
  readonly corruptLegacyDrafts: number;
}

export interface NamedProjectRepository extends WorkingDraftRepository {
  initialize(): Promise<DraftRepositoryResult<RepositoryInitialization>>;
  saveRecovery(project: CoasterProject): Promise<DraftRepositoryResult<StoredDraft>>;
  loadRecovery(projectId: string): Promise<DraftRepositoryResult<StoredDraft>>;
  getPreference<T>(key: string): Promise<DraftRepositoryResult<T | null>>;
  setPreference<T>(key: string, value: T): Promise<DraftRepositoryResult<void>>;
  readonly durability: DraftDurability;
}

interface ProjectEnvelope {
  envelopeVersion: 2;
  projectId: string;
  revisionId: string;
  title: string;
  savedAt: string;
  canonicalHash: string;
  writeSequence: number;
  project: CoasterProject;
}

const DATABASE_NAME = 'buncha-coasters-editor';
const DATABASE_VERSION = 1;

function resultError<T>(
  code:
    | 'unavailable'
    | 'not_found'
    | 'corrupt'
    | 'read_failed'
    | 'write_failed'
    | 'delete_failed'
    | 'quota_exceeded',
  message: string,
  cause?: unknown,
): DraftRepositoryResult<T> {
  return {
    ok: false,
    error: { code, message, recoverable: true, ...(cause === undefined ? {} : { cause }) },
  };
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () =>
      reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('IndexedDB transaction failed'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains('projects')) {
        const store = database.createObjectStore('projects', { keyPath: 'projectId' });
        store.createIndex('savedAt', 'savedAt');
        store.createIndex('title', 'title');
      }
      if (!database.objectStoreNames.contains('recovery'))
        database.createObjectStore('recovery', { keyPath: 'projectId' });
      if (!database.objectStoreNames.contains('preferences'))
        database.createObjectStore('preferences', { keyPath: 'key' });
      if (!database.objectStoreNames.contains('legacyImports'))
        database.createObjectStore('legacyImports', { keyPath: 'sourceKey' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB could not be opened'));
    request.onblocked = () => reject(new Error('IndexedDB upgrade is blocked'));
  });
}

async function makeEnvelope(
  projectValue: CoasterProject,
  sequence: number,
): Promise<ProjectEnvelope> {
  const project = parseCoasterProject(projectValue);
  return {
    envelopeVersion: 2,
    projectId: project.projectId,
    revisionId: project.revisionId,
    title: project.metadata.title,
    savedAt: new Date().toISOString(),
    canonicalHash: await hashCoasterProject(project),
    writeSequence: sequence,
    project,
  };
}

async function validateEnvelope(value: unknown): Promise<StoredDraft> {
  if (!value || typeof value !== 'object') throw new Error('Draft envelope is missing');
  const envelope = value as Partial<ProjectEnvelope>;
  if (envelope.envelopeVersion !== 2 || !envelope.project || typeof envelope.savedAt !== 'string')
    throw new Error('Draft envelope is invalid');
  const loaded = loadCoasterProject(envelope.project);
  if (!loaded.ok) throw new Error(loaded.error.message);
  const canonicalHash = await hashCoasterProject(loaded.project);
  if (canonicalHash !== envelope.canonicalHash) throw new Error('Draft integrity check failed');
  return {
    projectId: loaded.project.projectId,
    revisionId: loaded.project.revisionId,
    title: loaded.project.metadata.title,
    savedAt: envelope.savedAt,
    canonicalHash,
    project: loaded.project,
  };
}

export class IndexedDbNamedProjectRepository implements NamedProjectRepository {
  readonly durability = 'durable' as const;
  private database: IDBDatabase | null = null;
  private sequence = 0;

  async initialize(): Promise<DraftRepositoryResult<RepositoryInitialization>> {
    if (this.database)
      return {
        ok: true,
        value: { durability: this.durability, importedLegacyDrafts: 0, corruptLegacyDrafts: 0 },
      };
    try {
      this.database = await openDatabase();
      const legacy = await this.importLegacyDrafts();
      return { ok: true, value: { durability: this.durability, ...legacy } };
    } catch (cause) {
      return resultError('unavailable', 'Durable draft storage is unavailable.', cause);
    }
  }

  async save(project: CoasterProject): Promise<DraftRepositoryResult<StoredDraft>> {
    const ready = await this.initialize();
    if (!ready.ok || !this.database)
      return resultError('unavailable', 'Durable draft storage is unavailable.');
    try {
      const envelope = await makeEnvelope(project, ++this.sequence);
      const transaction = this.database.transaction('projects', 'readwrite');
      transaction.objectStore('projects').put(envelope);
      await transactionDone(transaction);
      return { ok: true, value: await validateEnvelope(envelope) };
    } catch (cause) {
      const quota = cause instanceof DOMException && cause.name === 'QuotaExceededError';
      return resultError(
        quota ? 'quota_exceeded' : 'write_failed',
        quota ? 'Local draft storage is full.' : 'The draft could not be saved.',
        cause,
      );
    }
  }

  async load(projectId: string): Promise<DraftRepositoryResult<StoredDraft>> {
    const ready = await this.initialize();
    if (!ready.ok || !this.database)
      return resultError('unavailable', 'Durable draft storage is unavailable.');
    try {
      const transaction = this.database.transaction('projects', 'readonly');
      const raw = await requestResult(transaction.objectStore('projects').get(projectId));
      await transactionDone(transaction);
      if (!raw) return resultError('not_found', 'No saved draft exists for this coaster.');
      return { ok: true, value: await validateEnvelope(raw) };
    } catch (cause) {
      return resultError('corrupt', 'This saved draft is damaged.', cause);
    }
  }

  async list(): Promise<DraftRepositoryResult<StoredDraftSummary[]>> {
    const ready = await this.initialize();
    if (!ready.ok || !this.database)
      return resultError('unavailable', 'Durable draft storage is unavailable.');
    try {
      const transaction = this.database.transaction('projects', 'readonly');
      const values = await requestResult(transaction.objectStore('projects').getAll());
      await transactionDone(transaction);
      const summaries: StoredDraftSummary[] = [];
      for (const value of values) {
        try {
          const draft = await validateEnvelope(value);
          const { project, ...summary } = draft;
          void project;
          summaries.push(summary);
        } catch (cause) {
          const envelope = value as Partial<ProjectEnvelope>;
          summaries.push({
            projectId: String(envelope.projectId ?? `corrupt:${summaries.length}`),
            revisionId: String(envelope.revisionId ?? 'unknown'),
            title: String(envelope.title ?? 'Damaged draft'),
            savedAt: String(envelope.savedAt ?? new Date(0).toISOString()),
            canonicalHash: String(envelope.canonicalHash ?? '0'.repeat(64)),
            corrupt: true,
            errorMessage: cause instanceof Error ? cause.message : 'Draft is unreadable',
          });
        }
      }
      summaries.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
      return { ok: true, value: summaries };
    } catch (cause) {
      return resultError('read_failed', 'Saved drafts could not be listed.', cause);
    }
  }

  async delete(projectId: string): Promise<DraftRepositoryResult<void>> {
    const ready = await this.initialize();
    if (!ready.ok || !this.database)
      return resultError('unavailable', 'Durable draft storage is unavailable.');
    try {
      const transaction = this.database.transaction(['projects', 'recovery'], 'readwrite');
      transaction.objectStore('projects').delete(projectId);
      transaction.objectStore('recovery').delete(projectId);
      await transactionDone(transaction);
      return { ok: true, value: undefined };
    } catch (cause) {
      return resultError('delete_failed', 'The draft could not be deleted.', cause);
    }
  }

  async saveRecovery(project: CoasterProject): Promise<DraftRepositoryResult<StoredDraft>> {
    const ready = await this.initialize();
    if (!ready.ok || !this.database)
      return resultError('unavailable', 'Recovery storage is unavailable.');
    try {
      const envelope = await makeEnvelope(project, ++this.sequence);
      const transaction = this.database.transaction('recovery', 'readwrite');
      transaction.objectStore('recovery').put(envelope);
      await transactionDone(transaction);
      return { ok: true, value: await validateEnvelope(envelope) };
    } catch (cause) {
      return resultError('write_failed', 'A recovery checkpoint could not be saved.', cause);
    }
  }

  async loadRecovery(projectId: string): Promise<DraftRepositoryResult<StoredDraft>> {
    const ready = await this.initialize();
    if (!ready.ok || !this.database)
      return resultError('unavailable', 'Recovery storage is unavailable.');
    try {
      const transaction = this.database.transaction('recovery', 'readonly');
      const raw = await requestResult(transaction.objectStore('recovery').get(projectId));
      await transactionDone(transaction);
      if (!raw) return resultError('not_found', 'No recovery checkpoint is available.');
      return { ok: true, value: await validateEnvelope(raw) };
    } catch (cause) {
      return resultError('corrupt', 'The recovery checkpoint is damaged.', cause);
    }
  }

  async getPreference<T>(key: string): Promise<DraftRepositoryResult<T | null>> {
    const ready = await this.initialize();
    if (!ready.ok || !this.database)
      return resultError('unavailable', 'Editor preferences are unavailable.');
    try {
      const transaction = this.database.transaction('preferences', 'readonly');
      const value = (await requestResult(transaction.objectStore('preferences').get(key))) as
        { value?: T } | undefined;
      await transactionDone(transaction);
      return { ok: true, value: value?.value ?? null };
    } catch (cause) {
      return resultError('read_failed', 'Editor preferences could not be read.', cause);
    }
  }

  async setPreference<T>(key: string, value: T): Promise<DraftRepositoryResult<void>> {
    const ready = await this.initialize();
    if (!ready.ok || !this.database)
      return resultError('unavailable', 'Editor preferences are unavailable.');
    try {
      const transaction = this.database.transaction('preferences', 'readwrite');
      transaction.objectStore('preferences').put({ key, value });
      await transactionDone(transaction);
      return { ok: true, value: undefined };
    } catch (cause) {
      return resultError('write_failed', 'Editor preferences could not be saved.', cause);
    }
  }

  private async importLegacyDrafts(): Promise<{
    importedLegacyDrafts: number;
    corruptLegacyDrafts: number;
  }> {
    if (!this.database || typeof localStorage === 'undefined')
      return { importedLegacyDrafts: 0, corruptLegacyDrafts: 0 };
    let importedLegacyDrafts = 0;
    let corruptLegacyDrafts = 0;
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(LEGACY_V1_DRAFT_NAMESPACE)) keys.push(key);
    }
    for (const sourceKey of keys) {
      const raw = localStorage.getItem(sourceKey);
      if (!raw) continue;
      try {
        const markerTransaction = this.database.transaction('legacyImports', 'readonly');
        const marker = await requestResult(
          markerTransaction.objectStore('legacyImports').get(sourceKey),
        );
        await transactionDone(markerTransaction);
        if (marker) continue;
        const envelope = JSON.parse(raw) as { canonicalHash?: unknown; project?: unknown };
        const parsed = CoasterProjectV1Schema.parse(envelope.project);
        const legacyHash = await hashCoasterProjectV1(parsed);
        if (legacyHash !== envelope.canonicalHash)
          throw new Error('Legacy draft integrity check failed');
        const migrated = migrateV1ToV2(parsed);
        const existing = await this.load(migrated.projectId);
        if (!existing.ok && existing.error.code !== 'not_found')
          throw new Error(existing.error.message);
        if (!existing.ok) {
          const saved = await this.save(migrated);
          if (!saved.ok) throw new Error(saved.error.message);
          importedLegacyDrafts += 1;
        }
        const transaction = this.database.transaction('legacyImports', 'readwrite');
        transaction
          .objectStore('legacyImports')
          .put({ sourceKey, status: 'imported', attemptedAt: new Date().toISOString() });
        await transactionDone(transaction);
      } catch (cause) {
        corruptLegacyDrafts += 1;
        const transaction = this.database.transaction('legacyImports', 'readwrite');
        transaction.objectStore('legacyImports').put({
          sourceKey,
          status: 'corrupt',
          attemptedAt: new Date().toISOString(),
          error: cause instanceof Error ? cause.message : 'Unknown legacy error',
          sourceHash: canonicalStringify(raw),
        });
        await transactionDone(transaction);
      }
    }
    return { importedLegacyDrafts, corruptLegacyDrafts };
  }
}

class LimitedNamedProjectRepository implements NamedProjectRepository {
  readonly durability = 'limited' as const;
  private readonly projects = new LocalWorkingDraftRepository();
  private readonly recovery = new LocalWorkingDraftRepository(
    undefined,
    'buncha-coasters:recovery:v2:',
  );

  async initialize(): Promise<DraftRepositoryResult<RepositoryInitialization>> {
    return {
      ok: true,
      value: { durability: this.durability, importedLegacyDrafts: 0, corruptLegacyDrafts: 0 },
    };
  }
  save(project: CoasterProject) {
    return this.projects.save(project);
  }
  load(projectId: string) {
    return this.projects.load(projectId);
  }
  list() {
    return this.projects.list();
  }
  delete(projectId: string) {
    return this.projects.delete(projectId);
  }
  saveRecovery(project: CoasterProject) {
    return this.recovery.save(project);
  }
  loadRecovery(projectId: string) {
    return this.recovery.load(projectId);
  }
  async getPreference<T>(key: string): Promise<DraftRepositoryResult<T | null>> {
    try {
      const raw = localStorage.getItem(`buncha-coasters:preference:v2:${key}`);
      return { ok: true, value: raw === null ? null : (JSON.parse(raw) as T) };
    } catch (cause) {
      return resultError('read_failed', 'Editor preferences could not be read.', cause);
    }
  }
  async setPreference<T>(key: string, value: T): Promise<DraftRepositoryResult<void>> {
    try {
      localStorage.setItem(`buncha-coasters:preference:v2:${key}`, JSON.stringify(value));
      return { ok: true, value: undefined };
    } catch (cause) {
      return resultError('write_failed', 'Editor preferences could not be saved.', cause);
    }
  }
}

export class FailSoftNamedProjectRepository implements NamedProjectRepository {
  private active: NamedProjectRepository = new IndexedDbNamedProjectRepository();
  private initialized = false;
  get durability(): DraftDurability {
    return this.active.durability;
  }

  async initialize(): Promise<DraftRepositoryResult<RepositoryInitialization>> {
    if (this.initialized)
      return {
        ok: true,
        value: {
          durability: this.active.durability,
          importedLegacyDrafts: 0,
          corruptLegacyDrafts: 0,
        },
      };
    const result = await this.active.initialize();
    if (result.ok) {
      this.initialized = true;
      return result;
    }
    this.active = new LimitedNamedProjectRepository();
    this.initialized = true;
    return this.active.initialize();
  }
  private async ready(): Promise<NamedProjectRepository> {
    await this.initialize();
    return this.active;
  }
  async save(project: CoasterProject) {
    return (await this.ready()).save(project);
  }
  async load(projectId: string) {
    return (await this.ready()).load(projectId);
  }
  async list() {
    return (await this.ready()).list();
  }
  async delete(projectId: string) {
    return (await this.ready()).delete(projectId);
  }
  async saveRecovery(project: CoasterProject) {
    return (await this.ready()).saveRecovery(project);
  }
  async loadRecovery(projectId: string) {
    return (await this.ready()).loadRecovery(projectId);
  }
  async getPreference<T>(key: string) {
    return (await this.ready()).getPreference<T>(key);
  }
  async setPreference<T>(key: string, value: T) {
    return (await this.ready()).setPreference(key, value);
  }
}

export function createNamedProjectRepository(): NamedProjectRepository {
  return new FailSoftNamedProjectRepository();
}
